import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WatermarkContext = createContext();

export function WatermarkProvider({ children }) {
  const [watermarkEnabled, setWatermarkEnabled] = useState(true);
  const [priceVisible, setPriceVisible] = useState(true);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setWatermarkEnabled(response.data.watermark_enabled);
      setPriceVisible(response.data.price_visible !== false); // Default to true
    } catch (error) {
      // Default to enabled if fetch fails, log in dev for debugging
      if (process.env.NODE_ENV === 'development') {
        console.debug('Failed to fetch watermark settings:', error.message);
      }
      setWatermarkEnabled(true);
      setPriceVisible(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const toggleWatermark = useCallback(async (enabled) => {
    try {
      await axios.put(`${API}/settings`, { watermark_enabled: enabled, price_visible: priceVisible });
      setWatermarkEnabled(enabled);
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('Failed to toggle watermark:', error.message);
      }
      return false;
    }
  }, [priceVisible]);

  const togglePriceVisibility = useCallback(async (visible) => {
    try {
      await axios.put(`${API}/settings`, { watermark_enabled: watermarkEnabled, price_visible: visible });
      setPriceVisible(visible);
      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('Failed to toggle price visibility:', error.message);
      }
      return false;
    }
  }, [watermarkEnabled]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    watermarkEnabled,
    toggleWatermark,
    priceVisible,
    togglePriceVisibility,
    loading,
    refetch: fetchSettings
  }), [watermarkEnabled, toggleWatermark, priceVisible, togglePriceVisibility, loading, fetchSettings]);

  return (
    <WatermarkContext.Provider value={contextValue}>
      {children}
    </WatermarkContext.Provider>
  );
}

export function useWatermark() {
  const context = useContext(WatermarkContext);
  if (!context) {
    throw new Error('useWatermark must be used within a WatermarkProvider');
  }
  return context;
}
